import {Panes} from "../controllers/panes";
import {ICoordinateAdjustmentService} from "../services/coordinate-adjustment";
import {Events} from "../lt-ng/landcare-toolkit-events";
import {FeaturesEvents} from "../services/events/FeaturesEvents";
import {GeoAppEvents} from "../controllers/events/geoapp_events";
import {LTServices} from "../lt-ng/lt-services";
import IMapService = LTServices.IMapService;
import * as SMapTourSteps from "./steps";
import { LoggingEvent } from "../services/IUsageLoggingService";
import {MapClickInteraction, simulatedClickEvent} from "../lt-ng/map-interactions";
import {Map as OlMap} from 'ol';

export function simulateOpenLayersEvent(map: OlMap, interaction: MapClickInteraction, type, x, y, opt_shiftKey = undefined) {
    let viewport = map.getViewport();

    let position = viewport.getBoundingClientRect();
    const mbe = simulatedClickEvent([position.left + x + (position.width / 2), position.top + y + (position.height / 2)], map);

    interaction.simulateEvent(mbe);
}

export function onActivate() {
    document.querySelector('body').classList.add('tour-active');
}

export function onReset() {
   document.querySelector('body').classList.remove('tour-active');
}

const DEBUG_WAIT_FOR = true;

export function waitFor(cssSelector: string, timeLimit=10000): Promise<HTMLElement> {
    if (DEBUG_WAIT_FOR) {
        console.log('waiting for', cssSelector);
    }

    return new Promise<HTMLElement>(function(resolve, reject) {
        let tries = 0;

        const tryToFindIt = () => {
            tries += 1;

            const elem = document.querySelector(cssSelector);

            if (elem instanceof HTMLElement) {
                resolve(elem);
            } else if (tries * 16 < timeLimit) {
                requestAnimationFrame(tryToFindIt);
            } else {
                if (DEBUG_WAIT_FOR) {
                    console.log('failed to find', cssSelector);
                }
                reject('time limit is up');
            }
        };

        requestAnimationFrame(tryToFindIt);
    });
}

export function delay(time: number): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            resolve();
        }, time);
    })
}
export function togglePane(appScope: ng.IScope, paneId: string, onOrOff = true): Promise<any> {
    return new Promise<any>(function(resolve) {
        const doIt = () => {
            appScope.$broadcast(onOrOff ? Panes.Events.SHOW_PANE : Panes.Events.HIDE_PANE, paneId, onOrOff);
            resolve();
        };

        if (appScope.$digest) doIt();
        else {
            appScope.$apply(doIt);
        }
    });
}

// will refresh driver.js only if the popover is outside the window
export function lazyPopupRefresh(driver) {
    const elem = document.getElementById('driver-popover-item');
    if (!elem) return;

    const bbox = elem.getBoundingClientRect();

    const h = window.innerHeight;
    const w = window.innerWidth;

    const refresh = ((bbox.top + bbox.height * 0.8) > h) // < 80% vertically visible
        || ((bbox.top + bbox.height * 0.2) < 0)
        || ((bbox.left + bbox.width * 0.2) < 0)
        || ((bbox.right - bbox.width * 0.2) > w);

    if (refresh && driver.hasHighlightedElement()) {
        (<any> driver).refresh()
    }
}

export function startWhenReady(driver, appScope, appElement, mapService: IMapService, desktop = false): Promise<any> {
    return waitFor('.ol-viewport').then(() => {
        let tries = 0;
        const interval = setInterval(() => {
            appScope = appScope || window['angular'].element(appElement).scope();
            tries++;
            if (appScope && mapService) {
                clearInterval(interval);
                mapService.mapPromise.then(() => {
                    appScope.$broadcast(FeaturesEvents.hideReport);
                    togglePane(appScope, Panes.Ids.FEATURES, false);
                    togglePane(appScope, Panes.Ids.LAYERS, desktop);

                    appScope.$broadcast(GeoAppEvents.resetMapAndLayers);

                    const printPane = (<HTMLElement> document.querySelector('.print-pane .pane-heading'));
                    if(printPane) printPane.click();

                    const dismissModal = (<HTMLElement> document.querySelector('[data-dismiss="modal"]'));
                    if (dismissModal) dismissModal.click();

                    driver.start(0);

                    setTimeout(() => lazyPopupRefresh(driver), 100);
                });
            } else if (tries * 25 > 10000) {
                clearInterval(interval);
                timeoutHandler(driver)();
            }
        }, 25);
    }).catch(timeoutHandler(driver));
}

export function timeoutHandler(driver: any) {
    return (e?: Error|undefined) => {
        if (!driver || !driver['isActivated']) {
            console.trace();
            onReset();
            return;
        }

        driver.defineSteps([{
            _id: 'errorTimeout',
            element: 'main > div',
            popover: {
                ...SMapTourSteps.errorTimeout,
                position: 'mid-center'
            }
        }]);
        driver.start(0);
    };
}

export function _workAroundMoveNext(driver: any, steps: any[], id: string) {
    if (DEBUG_WAIT_FOR) {
        console.log('workAroundMoveNext beginning move to:', id);
    }

    // work around for this https://github.com/kamranahmedse/driver.js/issues/121#issuecomment-407045277
    // if there is an overlay, make sure its the correct opacity, for some reason, this will sometimes break
    const step = steps.find(s => s['_id'] == id);

    waitFor(step.element)
        .then((elem) => {
            elem.scrollIntoView(true);
            const overlay = (<HTMLElement> document.querySelector('#driver-page-overlay'));
            if (overlay) overlay.style.opacity = '0.75';

            driver.defineSteps(steps);

            // use the id the find the step we're interested, numeric indexes are unstable, because the number of steps in the driver object === the number of steps with extant element
            const stepIndex = driver['steps'].findIndex(s => (<any> s).options['_id'] === id);
            if (stepIndex === -1) {
                const err = new Error('step not found, is the element visible?');
                console.error(err);
                throw err;
            }
            console.log('workAroundMoveNext moving to:', id, 'at index', stepIndex);
            driver.start(stepIndex);

            // refresh after a while to make sure the element is visible on the screen
            setTimeout(() => lazyPopupRefresh(driver), 100);
        }, timeoutHandler(driver));
    // this is even is used in logging to log the tour next steps
    // const nextEvent = new Event('tournext');
    // nextEvent['id'] = id;
    // window.dispatchEvent(nextEvent);
    const nextLoggingEvent = <LoggingEvent> new Event("LOG_EVENT");
    nextLoggingEvent.category = "TOUR_NEXT";
    nextLoggingEvent.action = `step: ${id}`;
    window.dispatchEvent(nextLoggingEvent);
}

export interface IStepLike {
    _id: string;
    [key: string]: any;
}

export type TourStatus = () => {
    driver: any;
    steps: IStepLike[];
    onOrientationChange?: () => void;
    ready?: Promise<any>;
}

export function resetMap($scope: any, coordinateAdjustmentService: ICoordinateAdjustmentService) {
    $scope.$broadcast(Events.OL_RESET_EXTENT, {
        padding: coordinateAdjustmentService.padding(),
        duration: 500
    });
}

export function moveMap(map: OlMap, coordinateAdjustmentService: ICoordinateAdjustmentService, coordinate: any, zoomLevel: any) {
    const view = <any> map.getView();
    const coord = coordinateAdjustmentService.adjustCentreForScreenSpace(coordinate, <any> map, zoomLevel);
    view.setCenter(coord);
    view.setZoom(9);
}

